// Settings → Backup & Restore panel. Lists database backups, triggers a manual
// backup, downloads a snapshot, deletes manual/pre-update backups, and runs a
// (destructive) restore behind a typed confirmation. Gated by the `system.backup`
// permission both here and on the server.
import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';

interface Backup {
  id: string;
  name: string;
  type: 'daily' | 'weekly' | 'monthly' | 'manual' | 'pre_update';
  app_version: string | null;
  file_size: number;
  created_at: string;
  expires_at: string | null;
}

function formatBytes(n: number): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`;
}

@customElement('cadence-backup-settings')
export class CadenceBackupSettings extends LitElement {
  @state() private backups: Backup[] = [];
  @state() private loading = true;
  @state() private newName = '';
  @state() private creating = false;
  // Restore confirmation modal
  @state() private restoreTarget: Backup | null = null;
  @state() private confirmText = '';
  @state() private restoring = false;

  static styles = css`
    :host { display: block; }
    .panel { padding-top: var(--space-4); display: grid; gap: var(--space-4); }
    .row { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; }
    .list { display: flex; flex-direction: column; gap: 8px; }
    .item { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 14px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); }
    .left { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .name { font-weight: 700; }
    .meta { color: var(--color-text-secondary); font-size: 0.82rem; }
    .actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .act { cursor: pointer; background: none; border: 1px solid var(--color-border); border-radius: var(--radius-sm); padding: 4px 10px; font: inherit; font-size: 0.82rem; color: var(--color-text-primary); text-decoration: none; }
    .act:hover { border-color: var(--color-primary); color: var(--color-primary); }
    .rm { cursor: pointer; color: var(--color-danger); background: none; border: none; font-size: 0.85rem; }
    .muted { color: var(--color-text-secondary); font-size: 0.85rem; }
    .badge { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 7px; border-radius: 999px; border: 1px solid var(--color-border); color: var(--color-text-secondary); }
    .warn { color: var(--color-danger); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try { this.backups = await api.get<Backup[]>('/system/backups'); }
    catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private async createBackup() {
    const name = this.newName.trim();
    if (!name) { showToast(t('settings.toast.backupNameRequired'), 'error'); return; }
    this.creating = true;
    try {
      await api.post('/system/backups', { name });
      this.newName = '';
      showToast(t('settings.toast.backupCreated'), 'success');
      await this.load();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.creating = false; }
  }

  private async deleteBackup(b: Backup) {
    try {
      await api.del(`/system/backups/${b.id}`);
      showToast(t('settings.toast.backupDeleted'), 'success');
      await this.load();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async confirmRestore() {
    if (!this.restoreTarget || this.confirmText !== 'RESTORE') return;
    this.restoring = true;
    try {
      const res = await api.post<{ tables: number; rows: number; version_warning: string | null }>(
        `/system/backups/${this.restoreTarget.id}/restore`, { confirm: 'RESTORE' },
      );
      this.restoreTarget = null;
      this.confirmText = '';
      showToast(res.rows === 1 ? t('settings.toast.restoredOne', { rows: res.rows, tables: res.tables }) : t('settings.toast.restoredMany', { rows: res.rows, tables: res.tables }), 'success');
      if (res.version_warning) showToast(res.version_warning, 'error');
      await this.load();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.restoring = false; }
  }

  private openRestore(b: Backup) { this.restoreTarget = b; this.confirmText = ''; }
  private closeRestore() { this.restoreTarget = null; this.confirmText = ''; }

  private renderItem(b: Backup) {
    const when = new Date(b.created_at).toLocaleString();
    const deletable = b.type === 'manual' || b.type === 'pre_update';
    return html`<div class="item">
      <div class="left">
        <span class="name">${b.name} <span class="badge">${t(`settings.backup.types.${b.type}`)}</span></span>
        <span class="meta">${when} · ${formatBytes(b.file_size)}${b.app_version ? ` · v${b.app_version}` : ''}${b.expires_at ? ` · ${t('settings.backup.expiresSuffix', { date: new Date(b.expires_at).toLocaleDateString() })}` : ''}</span>
      </div>
      <div class="actions">
        <a class="act" href="/api/v1/system/backups/${b.id}/download">${t('settings.backup.download')}</a>
        <button class="act" @click=${() => this.openRestore(b)}>${t('settings.backup.restore')}</button>
        ${deletable
          ? html`<button class="rm" @click=${() => this.deleteBackup(b)}>${t('settings.actions.delete')}</button>`
          : html`<span class="muted" title=${t('settings.backup.retentionTitle')}>${t('settings.backup.retentionManaged')}</span>`}
      </div>
    </div>`;
  }

  render() {
    return html`<div class="panel">
      <cadence-card heading=${t('settings.backup.createHeading')}>
        <p class="muted" style="margin-top:0">${t('settings.backup.createHint')}</p>
        <div class="row">
          <cadence-input label=${t('settings.backup.nameLabel')} placeholder=${t('settings.backup.namePlaceholder')} .value=${this.newName}
            @input=${(e: CustomEvent) => (this.newName = e.detail)} @enter=${() => this.createBackup()}></cadence-input>
          <cadence-button ?loading=${this.creating} @click=${() => this.createBackup()}>${t('settings.backup.create')}</cadence-button>
        </div>
      </cadence-card>

      <cadence-card heading=${t('settings.backup.listHeading')}>
        ${this.loading
          ? html`<cadence-loading></cadence-loading>`
          : this.backups.length
            ? html`<div class="list">${this.backups.map((b) => this.renderItem(b))}</div>`
            : html`<cadence-empty-state heading=${t('settings.backup.emptyHeading')} message=${t('settings.backup.emptyMessage')}></cadence-empty-state>`}
      </cadence-card>

      ${this.renderRestoreModal()}
    </div>`;
  }

  private renderRestoreModal() {
    const b = this.restoreTarget;
    if (!b) return '';
    return html`
      <cadence-modal open heading=${t('settings.backup.restoreHeading')} @close=${() => this.closeRestore()}>
        <p style="margin-top:0">${t('settings.backup.restoreBody1')} <strong>${b.name}</strong> ${t('settings.backup.restoreBody2', { date: new Date(b.created_at).toLocaleString() })} <strong class="warn">${t('settings.backup.restoreReplace')}</strong> ${t('settings.backup.restoreBody3')}</p>
        <p class="muted">${t('settings.backup.typeConfirm1')} <strong>RESTORE</strong> ${t('settings.backup.typeConfirm2')}</p>
        <cadence-input .value=${this.confirmText} placeholder="RESTORE"
          @input=${(e: CustomEvent) => (this.confirmText = e.detail)}></cadence-input>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => this.closeRestore()}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button variant="danger" ?disabled=${this.confirmText !== 'RESTORE'} ?loading=${this.restoring}
            @click=${() => this.confirmRestore()}>${t('settings.backup.restoreDb')}</cadence-button>
        </div>
      </cadence-modal>`;
  }
}
